import { useState } from 'react';
import { supabase } from '../../../lib/supabaseClient';

interface TipModalProps {
  jobId: string;
  providerName: string;
  onClose: () => void;
}

export default function TipModal({ jobId, providerName, onClose }: TipModalProps) {
  const [selectedTip, setSelectedTip] = useState<number | null>(null);
  const [customTip, setCustomTip] = useState('');
  const [processing, setProcessing] = useState(false);

  const quickTips = [5, 10, 15, 20];

  const handleTipSubmit = async () => {
    const tipAmount = selectedTip === 0 ? parseFloat(customTip) : selectedTip;

    if (!tipAmount || tipAmount <= 0) {
      alert('Please select or enter a valid tip amount');
      return;
    }

    setProcessing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-tip-payment`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`
          },
          body: JSON.stringify({
            jobId,
            tipAmount
          })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to process tip');
      }

      // Redirect to Stripe checkout
      window.location.href = result.checkoutUrl;

    } catch (error: any) {
      console.error('Tip error:', error);
      alert(error.message || 'Failed to process tip. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={(e) => e.target === e.currentTarget && !processing && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-hand-coin-line text-3xl text-white"></i>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Leave a Tip</h3>
          <p className="text-gray-600">
            Show your appreciation for {providerName}'s excellent service
          </p>
          <p className="text-sm text-teal-600 font-semibold mt-2">
            ✨ Provider receives 100% of your tip - no fees!
          </p>
        </div>

        {/* Quick Tip Options */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          {quickTips.map((amount) => (
            <button
              key={amount}
              onClick={() => {
                setSelectedTip(amount);
                setCustomTip('');
              }}
              className={`py-3 rounded-xl font-semibold transition-all cursor-pointer whitespace-nowrap ${
                selectedTip === amount
                  ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-lg scale-105'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              £{amount}
            </button>
          ))}
        </div>

        {/* Custom Amount */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Or enter custom amount
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">
              £
            </span>
            <input
              type="number"
              min="1"
              step="0.01"
              value={customTip}
              onChange={(e) => {
                setCustomTip(e.target.value);
                setSelectedTip(0);
              }}
              placeholder="0.00"
              className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-400 focus:outline-none text-lg font-semibold"
            />
          </div>
        </div>

        {/* Summary */}
        {(selectedTip || customTip) && (
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 mb-6 border border-amber-200">
            <div className="flex justify-between items-center">
              <span className="text-gray-700 font-medium">Tip Amount</span>
              <span className="text-2xl font-bold text-amber-600">
                £{(selectedTip === 0 ? parseFloat(customTip || '0') : selectedTip)?.toFixed(2)}
              </span>
            </div>
            <p className="text-xs text-gray-600 mt-2">
              <i className="ri-shield-check-line text-teal-600"></i> Secure payment via Stripe
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={processing}
            className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-semibold whitespace-nowrap cursor-pointer disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleTipSubmit}
            disabled={processing || (!selectedTip && !customTip)}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-xl hover:from-amber-500 hover:to-orange-600 transition-all font-semibold shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {processing ? (
              <>
                <i className="ri-loader-4-line animate-spin mr-2"></i>
                Processing...
              </>
            ) : (
              <>
                <i className="ri-gift-line mr-2"></i>
                Send Tip
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}